"use client";

import { useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Badge } from "@/app/components/ui/badge";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Slider } from "@/app/components/ui/slider";
import { Checkbox } from "@/app/components/ui/checkbox";
import { CopyButton } from "@/app/components/ui/copy-button";
import { 
  Hash, 
  Copy, 
  Download, 
  RotateCcw, 
  Settings, 
  Info, 
  AlertTriangle, 
  CheckCircle,
  Key,
  Shield,
  Zap,
  BarChart3,
  History,
  Save,
  Trash2,
  RefreshCw,
  Database,
  Lock,
  Clock,
  Globe,
  Fingerprint,
  Layers,
  Target,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

interface GeneratedCUID2 {
  id: string;
  value: string;
  timestamp: Date;
  length: number;
  type: string;
  settings: {
    length: number;
    prefix?: string;
    customAlphabet?: string;
  };
}

interface CUID2History {
  id: string;
  cuids: string[];
  timestamp: Date;
  count: number;
  settings: any;
}

type GeneratorType = "standard" | "custom-length" | "prefixed" | "custom-alphabet";

// Client-side alphabet definition (kept for reference, actual generation is done server-side via TRPC)
const DEFAULT_ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz";

export default function CUID2GeneratorPage() {
  const [generatedCUIDs, setGeneratedCUIDs] = useState<GeneratedCUID2[]>([]);
  const [history, setHistory] = useState<CUID2History[]>([]);
  const [activeTab, setActiveTab] = useState<GeneratorType>("standard");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Standard settings
  const [count, setCount] = useState(1);
  const [standardLength, setStandardLength] = useState(24);

  // Custom length settings
  const [customLength, setCustomLength] = useState(24);

  // Prefixed settings
  const [prefix, setPrefix] = useState("");
  const [prefixedLength, setPrefixedLength] = useState(24);

  // Custom alphabet settings
  const [customAlphabet, setCustomAlphabet] = useState("0123456789abcdefghijklmnopqrstuvwxyz");
  const [alphabetLength, setAlphabetLength] = useState(24);

  // TRPC mutations
  const generateStandardMutation = trpc.cuid2.generateStandard.useMutation();
  const generateCustomLengthMutation = trpc.cuid2.generateCustomLength.useMutation();
  const generatePrefixedMutation = trpc.cuid2.generatePrefixed.useMutation();
  const generateCustomAlphabetMutation = trpc.cuid2.generateCustomAlphabet.useMutation();

  const generateCUIDs = useCallback(async () => {
    try {
      let result: any;
      let type = "";
      let settings: any = {};

      switch (activeTab) {
        case "standard":
          result = await generateStandardMutation.mutateAsync({
            count,
            length: standardLength
          });
          type = `Standard CUID2 (${standardLength} chars)`;
          settings = { length: standardLength, count };
          break;
        case "custom-length":
          result = await generateCustomLengthMutation.mutateAsync({
            count,
            length: customLength
          });
          type = `Custom Length (${customLength} chars)`;
          settings = { length: customLength, count };
          break;
        case "prefixed":
          result = await generatePrefixedMutation.mutateAsync({
            count,
            length: prefixedLength,
            prefix
          });
          type = `Prefixed (${prefix}${prefixedLength} chars)`;
          settings = { length: prefixedLength, prefix, count };
          break;
        case "custom-alphabet":
          result = await generateCustomAlphabetMutation.mutateAsync({
            count,
            length: alphabetLength,
            customAlphabet
          });
          type = `Custom Alphabet (${alphabetLength} chars)`;
          settings = { length: alphabetLength, customAlphabet, count };
          break;
      }

      if (result.success) {
        const cuids = result.cuids;

        // Add to generated CUIDs
        const newGeneratedCUIDs = cuids.map((value: string, index: number) => ({
          id: `${Date.now()}-${index}`,
          value,
          timestamp: new Date(),
          length: value.length,
          type,
          settings
        }));

        setGeneratedCUIDs(prev => [...newGeneratedCUIDs, ...prev]);

        // Add to history
        const historyEntry: CUID2History = {
          id: Date.now().toString(),
          cuids,
          timestamp: new Date(),
          count: cuids.length,
          settings
        };

        setHistory(prev => [historyEntry, ...prev.slice(0, 49)]); // Keep last 50 entries

        toast.success(`Generated ${cuids.length} CUID2${cuids.length > 1 ? 's' : ''}!`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to generate CUID2s";
      toast.error(errorMessage);
    }
  }, [activeTab, count, standardLength, customLength, prefix, prefixedLength, customAlphabet, alphabetLength, generateStandardMutation, generateCustomLengthMutation, generatePrefixedMutation, generateCustomAlphabetMutation]);

  const copyToClipboard = useCallback(async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (index !== undefined) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setGeneratedCUIDs([]);
    toast.success("History cleared!");
  }, []);

  const exportCUIDs = useCallback(() => {
    const data = {
      generated: generatedCUIDs,
      history: history,
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cuid2-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("CUID2s exported!");
  }, [generatedCUIDs, history]);

  const validateAlphabet = useCallback((alphabet: string) => {
    if (alphabet.length < 2) {
      return "Alphabet must have at least 2 characters";
    }
    if (alphabet.length > 256) {
      return "Alphabet cannot exceed 256 characters";
    }
    const uniqueChars = new Set(alphabet).size;
    if (uniqueChars !== alphabet.length) {
      return "Alphabet must contain unique characters only";
    }
    return null;
  }, []);

  const getTotalLength = useMemo(() => {
    return generatedCUIDs.reduce((sum, item) => sum + item.length, 0);
  }, [generatedCUIDs]);

  const getAverageLength = useMemo(() => {
    if (generatedCUIDs.length === 0) return 0;
    return getTotalLength / generatedCUIDs.length;
  }, [generatedCUIDs, getTotalLength]);

  const getUniqueCount = useMemo(() => {
    const uniqueValues = new Set(generatedCUIDs.map(item => item.value));
    return uniqueValues.size;
  }, [generatedCUIDs]);

  const alphabetError = validateAlphabet(customAlphabet);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">CUID2 Generator</h1>
        <p className="text-muted-foreground">
          Generate collision-resistant unique identifiers (CUID2) that are more secure and performant than traditional UUIDs.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator Controls */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5" />
                CUID2 Generator
              </CardTitle>
              <CardDescription>
                Choose your generator type and configure the settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as GeneratorType)}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="standard">Standard</TabsTrigger>
                  <TabsTrigger value="custom-length">Custom Length</TabsTrigger>
                  <TabsTrigger value="prefixed">Prefixed</TabsTrigger>
                  <TabsTrigger value="custom-alphabet">Custom Alphabet</TabsTrigger>
                </TabsList>

                <TabsContent value="standard" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Length: {standardLength} characters</Label>
                    <Slider
                      value={[standardLength]}
                      onValueChange={(value) => setStandardLength(value[0])}
                      max={50}
                      min={8}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Standard CUID2 uses the default alphabet (0-9, a-z) and includes timestamp and counter for uniqueness.
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="custom-length" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Custom Length: {customLength} characters</Label>
                    <Slider
                      value={[customLength]}
                      onValueChange={(value) => setCustomLength(value[0])}
                      max={100}
                      min={8}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Custom length CUID2 allows you to specify any length between 8 and 100 characters.
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="prefixed" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="prefix">Prefix</Label>
                    <Input
                      id="prefix"
                      placeholder="user_"
                      value={prefix}
                      onChange={(e) => setPrefix(e.target.value)}
                      maxLength={10}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Length: {prefixedLength} characters</Label>
                    <Slider
                      value={[prefixedLength]}
                      onValueChange={(value) => setPrefixedLength(value[0])}
                      max={50}
                      min={Math.max(8, prefix.length + 8)}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Prefixed CUID2 adds a custom prefix to make your identifiers more descriptive (e.g., user_abc123...).
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="custom-alphabet" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="customAlphabet">Custom Alphabet</Label>
                    <Input
                      id="customAlphabet"
                      placeholder="0123456789abcdefghijklmnopqrstuvwxyz"
                      value={customAlphabet}
                      onChange={(e) => setCustomAlphabet(e.target.value)}
                    />
                    {alphabetError && (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{alphabetError}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Length: {alphabetLength} characters</Label>
                    <Slider
                      value={[alphabetLength]}
                      onValueChange={(value) => setAlphabetLength(value[0])}
                      max={50}
                      min={8}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Custom alphabet CUID2 uses your specified character set. Must contain unique characters only.
                    </AlertDescription>
                  </Alert>
                </TabsContent>
              </Tabs>

              <div className="flex gap-2">
                <Button 
                  onClick={generateCUIDs} 
                  className="flex-1"
                  disabled={
                    activeTab === "custom-alphabet" && !!alphabetError ||
                    generateStandardMutation.isPending ||
                    generateCustomLengthMutation.isPending ||
                    generatePrefixedMutation.isPending ||
                    generateCustomAlphabetMutation.isPending
                  }
                >
                  {(generateStandardMutation.isPending ||
                    generateCustomLengthMutation.isPending ||
                    generatePrefixedMutation.isPending ||
                    generateCustomAlphabetMutation.isPending) ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Hash className="h-4 w-4 mr-2" />
                      Generate CUID2s
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={clearHistory}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear
                </Button>
                <Button variant="outline" onClick={exportCUIDs}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Generated CUID2s Display */}
          {generatedCUIDs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Generated CUID2s
                </CardTitle>
                <CardDescription>
                  {generatedCUIDs.length} CUID2{generatedCUIDs.length > 1 ? 's' : ''} generated
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  {generatedCUIDs.slice(0, 20).map((item, index) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="text-xs">
                          {item.length} chars
                        </Badge>
                        <code className="text-sm font-mono break-all">{item.value}</code>
                      </div>
                      <CopyButton
                        text={item.value}
                        onCopy={() => copyToClipboard(item.value, index)}
                        className="h-8 w-8"
                      />
                    </div>
                  ))}
                </div>
                {generatedCUIDs.length > 20 && (
                  <p className="text-sm text-muted-foreground">
                    Showing first 20 CUID2s. Total: {generatedCUIDs.length}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Statistics and History */}
        <div className="space-y-6">
          {/* Statistics */}
          {generatedCUIDs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{generatedCUIDs.length}</div>
                    <div className="text-sm text-muted-foreground">Total CUID2s</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getUniqueCount}</div>
                    <div className="text-sm text-muted-foreground">Unique</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getAverageLength.toFixed(1)}</div>
                    <div className="text-sm text-muted-foreground">Avg Length</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getTotalLength}</div>
                    <div className="text-sm text-muted-foreground">Total Chars</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Recent History
                </CardTitle>
                <CardDescription>
                  Last {history.length} generation{history.length > 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.slice(0, 10).map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {entry.count} CUID2s
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp || Date.now()).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {entry.cuids.slice(0, 3).map((cuid, index) => (
                          <code key={index} className="text-xs font-mono block break-all">
                            {cuid}
                          </code>
                        ))}
                        {entry.cuids.length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{entry.cuids.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* CUID2 Benefits */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                CUID2 Benefits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Lock className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Collision Resistant</div>
                  <div className="text-muted-foreground">Extremely low probability of duplicates</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">High Performance</div>
                  <div className="text-muted-foreground">Faster generation than UUIDs</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Time Sortable</div>
                  <div className="text-muted-foreground">Contains timestamp for ordering</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Globe className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">URL Safe</div>
                  <div className="text-muted-foreground">Safe to use in URLs and databases</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Fingerprint className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Compact</div>
                  <div className="text-muted-foreground">Shorter than UUIDs (24 chars vs 36)</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Use prefixed CUID2s for different entity types (user_, post_, etc.)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>CUID2s are perfect for database primary keys</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Custom alphabets can be used for specific character sets</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Shorter lengths are suitable for user-facing IDs</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
